/** Settings → API Tokens. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, THead, SecretReveal } from "./shared";
import {
  useApiTokens, useCreateApiToken, useRevokeApiToken,
  type ApiTokenCreated,
} from "@/hooks/security/useEdrAdmin";

const SCOPES = [
  "alerts:read", "alerts:write",
  "events:read", "events:write",
  "agents:read",
  "research:read",
  "all:read",
];

const EXPIRY_OPTIONS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "2 years", days: 730 },
  { label: "Never", days: null },
];

export function ApiTokensSection() {
  const q = useApiTokens();
  const [createOpen, setCreateOpen] = useState(false);
  const unavail = q.data?.kind === "unavailable";
  const tokens = q.data?.kind === "ok" ? q.data.data.tokens : [];
  const revoke = useRevokeApiToken();

  return (
    <Panel
      title={`API tokens (${tokens.length})`}
      actions={<Button size="sm" onClick={() => setCreateOpen(true)} disabled={unavail}><Plus className="h-3.5 w-3.5 mr-1" /> Create token</Button>}
      loading={q.isLoading}
      isError={q.isError}
      error={q.error}
      empty={!q.isLoading && (unavail || tokens.length === 0)}
      emptyTitle={unavail ? "Coming online — Q3 2026" : "No tokens yet"}
      emptyDescription={unavail ? "Token endpoint isn't live yet." : "Create a token for SIEM, CI, or webhooks."}
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <THead cols={["Name", "Scopes", "Last used", "Expires", ""]} />
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2">
                  <div className="font-medium text-foreground">{t.name}</div>
                  {t.createdBy && <div className="text-[10px] text-muted-foreground">by {t.createdBy}</div>}
                </td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">
                  {t.scopes.map((s) => <code key={s} className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">{s}</code>)}
                </div></td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">
                  {t.lastUsedAt ? formatDistanceToNow(new Date(t.lastUsedAt), { addSuffix: true }) : "never"}
                </td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">
                  {t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : "never"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={revoke.isPending}
                    onClick={() => { if (confirm(`Revoke ${t.name}? Connected integrations will break.`)) revoke.mutate(t.id); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateTokenDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Panel>
  );
}

function CreateTokenDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<string>("365");
  const [scopes, setScopes] = useState<Set<string>>(new Set(["alerts:read"]));
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const create = useCreateApiToken();

  const reset = () => { setName(""); setExpiry("365"); setScopes(new Set(["alerts:read"])); setCreated(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{created ? "Token created" : "Create an API token"}</DialogTitle>
          <DialogDescription>
            {created ? "Copy this token now — it's only shown once." : "Pick scopes carefully — tokens act with full privileges within their scopes."}
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-3 py-2">
            <SecretReveal value={created.token} />
            <p className="text-[11px] text-muted-foreground">
              Use it as: <code className="font-mono text-[10px]">curl -H "Authorization: Bearer {created.token.slice(0, 12)}…" …</code>
            </p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Splunk integration" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Expires</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => <SelectItem key={o.label} value={String(o.days ?? "never")}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Scopes</Label>
              <div className="rounded border border-border p-2 space-y-1.5">
                {SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-xs">
                    <Checkbox checked={scopes.has(s)} onCheckedChange={(c) => {
                      const next = new Set(scopes);
                      c ? next.add(s) : next.delete(s);
                      setScopes(next);
                    }} />
                    <code className="font-mono">{s}</code>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {created ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button disabled={!name || scopes.size === 0 || create.isPending} onClick={async () => {
                const r = await create.mutateAsync({
                  name, scopes: Array.from(scopes),
                  expiresInDays: expiry === "never" ? null : Number(expiry),
                });
                setCreated(r);
              }}>{create.isPending ? "Creating…" : "Create"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
